import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";
import ServiceStore from "../../../stores/ServiceStore";
import { Trailer, YardLocations, YardInventoryItem } from "../types/YardTypes";

// Legacy alias for backward compatibility
export interface YardLocation extends YardLocations {}

export class YardTrailersStore {
    rootStore: RootStore;

    // Loading states
    loading = false;
    error: string | null = null;

    // Selected items for operations
    selectedTrailerId: number | null = null;
    selectedLocationId: number | null = null;

    // Filter and search state
    filterStatus: string = "all";
    searchQuery: string = "";

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    // Service store accessors
    get trailerStore(): ServiceStore {
        return this.rootStore.trailerStore as ServiceStore;
    }

    get yardLocationsStore(): ServiceStore {
        return this.rootStore.yardLocationsStore as ServiceStore;
    }

    get yardInventoryItemsStore(): ServiceStore {
        return this.rootStore.yardInventoryItemsStore as ServiceStore;
    }

    // Computed values
    get trailers(): Trailer[] {
        return this.trailerStore.entities as unknown[] as Trailer[];
    }

    get yardLocations(): YardLocation[] {
        return this.yardLocationsStore.entities as unknown[] as YardLocation[];
    }

    get inventoryItems(): YardInventoryItem[] {
        return this.yardInventoryItemsStore.entities as unknown[] as YardInventoryItem[];
    }

    get selectedTrailer(): Trailer | null {
        if (!this.selectedTrailerId) return null;
        return this.trailers.find(trailer => trailer.id === this.selectedTrailerId) || null;
    }

    get selectedLocation(): YardLocation | null {
        if (!this.selectedLocationId) return null;
        return this.yardLocations.find(loc => loc.id === this.selectedLocationId) || null;
    }

    get filteredTrailers(): Trailer[] {
        let filtered = this.trailers;

        // Apply status filter
        if (this.filterStatus !== "all") {
            filtered = filtered.filter(trailer => trailer.status === this.filterStatus);
        }

        // Apply search filter
        if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            filtered = filtered.filter(trailer =>
                trailer.trailerNumber.toLowerCase().includes(query) ||
                trailer.trailerType.toLowerCase().includes(query) ||
                (trailer.licenseNumber && trailer.licenseNumber.toLowerCase().includes(query))
            );
        }

        return filtered;
    }

    get availableTrailers(): Trailer[] {
        return this.trailers.filter(trailer => trailer.status === 'available');
    }

    get trailersInUse(): Trailer[] {
        return this.trailers.filter(trailer => trailer.status === 'in_use');
    }

    get trailersInMaintenance(): Trailer[] {
        return this.trailers.filter(trailer => trailer.status === 'maintenance');
    }

    get trailersByType() {
        const typeGroups = this.trailers.reduce((acc, trailer) => {
            acc[trailer.trailerType] = (acc[trailer.trailerType] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        return typeGroups;
    }

    get companyOwnedTrailers(): Trailer[] {
        return this.trailers.filter(trailer => trailer.companyOwned);
    }

    get externalTrailers(): Trailer[] {
        return this.trailers.filter(trailer => !trailer.companyOwned);
    }

    // Actions
    setSelectedTrailer = (trailerId: number | null) => {
        this.selectedTrailerId = trailerId;
    };

    setSelectedLocation = (locationId: number | null) => {
        this.selectedLocationId = locationId;
    };

    setFilterStatus = (status: string) => {
        this.filterStatus = status;
    };

    setSearchQuery = (query: string) => {
        this.searchQuery = query;
    };

    setLoading = (loading: boolean) => {
        this.loading = loading;
    };

    setError = (error: string | null) => {
        this.error = error;
    };

    // Async actions
    refreshData = async () => {
        this.setLoading(true);
        this.setError(null);

        try {
            await Promise.all([
                this.trailerStore.find({ query: { $limit: 100 } }),
                this.yardLocationsStore.find({ query: { $limit: 50 } }),
                this.yardInventoryItemsStore.find({ query: { $limit: 50 } }),
            ]);
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to refresh data');
            console.error('Failed to refresh trailer data:', error);
        } finally {
            this.setLoading(false);
        }
    };

    createTrailer = async (trailerData: Partial<Trailer>) => {
        try {
            const newTrailer = await this.trailerStore.create({
                ...trailerData,
                status: 'available',
            });

            await this.refreshData();
            return newTrailer;
        } catch (error) {
            throw new Error(`Failed to create trailer: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    updateTrailer = async (trailerId: number, updates: Partial<Trailer>) => {
        try {
            await this.trailerStore.patch(trailerId, updates);
            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to update trailer: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    deleteTrailer = async (trailerId: number) => {
        try {
            await this.trailerStore.remove(trailerId);
            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to delete trailer: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    loadTrailer = async (trailerId: number, inventoryItemIds: number[], locationId: number) => {
        try {
            // Update trailer status to in_use and set location
            await this.trailerStore.patch(trailerId, {
                status: 'in_use',
                currentLocationId: locationId,
            });

            // Update inventory items to be associated with dispatch load
            for (const itemId of inventoryItemIds) {
                await this.yardInventoryItemsStore.patch(itemId, {
                    currentLocationId: locationId,
                    // Note: dispatchLoadId would be set when creating actual dispatch load
                });
            }

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to load trailer: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    unloadTrailer = async (trailerId: number, newLocationId: number) => {
        try {
            // Find inventory items currently on this trailer
            const trailerLocation = this.yardLocations.find(loc => 
                loc.id === this.selectedTrailer?.currentLocationId
            );

            if (trailerLocation) {
                const itemsOnTrailer = this.inventoryItems.filter(item => 
                    item.currentLocationId === trailerLocation.id
                );

                // Move items to new location
                for (const item of itemsOnTrailer) {
                    await this.yardInventoryItemsStore.patch(item.id, {
                        currentLocationId: newLocationId,
                    });
                }
            }

            // Update trailer status to available and clear location
            await this.trailerStore.patch(trailerId, {
                status: 'available',
                currentLocationId: null,
            });

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to unload trailer: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    clear = () => {
        runInAction(() => {
            this.selectedTrailerId = null;
            this.selectedLocationId = null;
            this.filterStatus = "all";
            this.searchQuery = "";
            this.error = null;
            this.loading = false;
        });
    };
}

export default YardTrailersStore;